import { filterResidentsByCategories } from '@/utils/residentCategories';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { ResidentListHeader } from '../../../components/residentListHeader';
import api from '../../../api';
import { ListItem } from './listItem';
import { selfDataSelector } from '../../../redux/config/selfData';
import { filterConditionSelector } from '../../../redux/config/residentListSettings';
import { usePermissionScopedData } from '@/hooks/usePermissionScopedData';
import { Alert } from '@mui/material';

const ListContainer = css`
  border-radius: 8px;
  overflow: hidden;
`;

const Container = css`
  padding: 0 16px 16px;
`;

const EmptyResident = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 16px;
`;

export const Advice: React.VFC = () => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { selfUser } = useSelector(selfDataSelector);
  const { categories } = useSelector(filterConditionSelector);
  const { t } = useTranslation();

  const permittedUnitId = selfUser.hasUnitPerm ? selfUser.nursingHomeUnitId : null;
  const { data, isFetching, hasFetchError, refresh } = usePermissionScopedData({
    nursingHomeId,
    userId: selfUser.id,
    serviceType: selfUser.serviceType,
    permittedUnitId,
    fetchData: async (unitId) => {
      // 選択ユニットにかかわらず、閲覧権限の範囲から最新50件を1回取得する。
      const [advices, residents, activeResidents] = await Promise.all([
        api.get('/nursing-homes/:nursingHomeId/advices', {
          params: { nursingHomeId, limit: 50, sort: 'desc', ...(unitId !== null ? { nursingHomeUnitId: unitId } : {}) },
        }),
        // ユニットの絞り込みに使う所属情報を入居者APIから取得する。
        api.get('/nursing-homes/:nursingHomeId/residents', {
          params: {
            nursingHomeId, limit: 10000,
            // eslint-disable-next-line camelcase
            ...(unitId !== null ? { nursing_home_unit_id: unitId } : {}),
          },
        }),
        api.get('/nursing-homes/:nursingHomeId/residents/active', { params: { nursingHomeId } }),
      ]);
      return { advices, residents, activeResidents };
    },
  });
  const residentAdviceList = React.useMemo(() => {
    if (!data) return [];
    const residentIds = new Set(filterResidentsByCategories(
      data.residents, data.activeResidents, categories, permittedUnitId,
    ).map((resident) => resident.id));
    const activeIds = new Set(data.activeResidents.map((resident) => resident.id));
    // 取得した50件を絞り込む。表示件数が減っても追加取得しない。
    return data.advices.filter((advice) => residentIds.has(advice.residentId) && activeIds.has(advice.residentId))
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [data, categories, permittedUnitId]);

  return (
    <div css={Container}>
      <ResidentListHeader
        title={t('advice', 'アドバイス')}
        categories={categories}
        onUpdate={refresh}
      />
      {hasFetchError ? (
        <Alert severity='error'>{t('@failedToLoadAdvice', 'アドバイスの取得に失敗しました')}</Alert>
      ) : !isFetching && !residentAdviceList.length ? (
        <div css={EmptyResident}>{t('noAdviceForNow', 'アドバイスはありません')}</div>
      ) : (
        <div css={ListContainer}>
          {residentAdviceList.map((advice) => (
            <ListItem key={advice.id} advice={advice} />
          ))}
        </div>
      )}
    </div>
  );
};
